/**
 * The wrapper every federated route renders inside.
 *
 * In the Next shell this was open-coded per page: most pages had nothing, /overview had
 * its own error boundary class, /eta-monitoring had a second one with different copy.
 * The result was that a remote failing to load showed a helpful message on two routes
 * and a blank screen on the other thirty-eight. One boundary, applied by the route
 * table, is the whole reason to have a route table.
 *
 * A remote load fails for reasons a page-level boundary is the right place to handle:
 * the app's host is down, its container 404s, or the shell outlived the build its chunk
 * names came from. The last one is recoverable and is handled a level up, in AppShell's
 * boundary, which reloads once — this one deliberately re-throws it rather than
 * swallowing it into a "failed to load" card the user would have to act on themselves.
 */

import React, { Component, Suspense, type ReactNode } from 'react';
import { Button, EmptyState } from '@syia-ai/maritimeai-ui';
import AppLoader from '@/components/AppLoader';
import { reportError } from '@/observability/clientLogger';
import { isStaleChunkError } from '@/utils/chunkRecovery';

interface Props {
  children: ReactNode;
  /** The remote's container name, e.g. `tmsa-app` — named in the failure message. */
  remote: string;
}

interface State {
  error: Error | null;
}

class RemoteErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Not reported for a stale chunk: render() re-throws that case to the shell-level
    // boundary, which reports it with the context that explains it.
    if (isStaleChunkError(error)) return;

    reportError(error, {
      event_detail: 'remote_load_failed',
      app: 'shell-app',
      remote: this.props.remote,
      componentStack: info?.componentStack,
    });
  }

  render() {
    if (!this.state.error) return this.props.children;

    // A document that outlived its build. Recoverable by reloading, which AppShell's
    // boundary does — so hand it up rather than showing a dead end here.
    if (isStaleChunkError(this.state.error)) throw this.state.error;

    return (
      <EmptyState
        className="shell-remote-error"
        headingLevel={2}
        title="This app didn't load"
        description={
          <>
            {this.props.remote} could not be reached. It may be deploying — reloading in a moment
            usually clears it. If it keeps happening, contact SIYA Support.
          </>
        }
        actions={<Button onClick={() => window.location.reload()}>Reload</Button>}
      />
    );
  }
}

export default function RemoteBoundary({ children, remote }: Props) {
  return (
    <RemoteErrorBoundary remote={remote}>
      <Suspense fallback={<AppLoader fill="area" />}>{children}</Suspense>
    </RemoteErrorBoundary>
  );
}
